import type { CaseStatus } from "@/api";
import { HANDOVER_CLOSED } from "@/lib/stages";

/**
 * Which tabs a Vorgang shows (design 2026-10-22 §6).
 *
 * The rule is one sentence: a tab stands there when its page has something to
 * show for this Vorgang — or when it is the page the person is on. No lock, no
 * greying out, no number, no tick: the progress has one display, the
 * Akte's stage path (B-4), and the tabs only say where one can go.
 *
 * It is a pure function of three facts — the Stand, the number of drawings and
 * the status — which `useCaseRecord` and `useCase` both deliver. The frame
 * never asks the reading: whether a Vorgang has a reading is exactly what the
 * *Auslese* tab would have to know, and the drawings stand in for it.
 *
 * Here and not in the component, so the table of cases in §6 is a test and not
 * a screen check.
 */

export type CaseTabKey = "akte" | "auslese" | "umriss" | "planung" | "kalkulation";

/** What the rule reads — nothing else about the Vorgang decides a tab. */
export interface CaseFacts {
  /** `latestVersionNo`: how many Stände the Vorgang has; 0 before the first. */
  stand: number;
  /** Documents that are drawings — the reason the *Auslese* has something to read. */
  drawings: number;
  status: CaseStatus;
}

export interface CaseTab {
  key: CaseTabKey;
  label: string;
  to: string;
  /** The tab of the page the person is on — the lit one. */
  current: boolean;
  /**
   * What the lit tab says it is: `"page"` when it leads to the very URL the
   * person is on, `"true"` when it marks the place and leads elsewhere — the
   * Eingang's „Akte" to the record, the reading's „Auslese" to /einlesen
   * (ruling R-5). The rest carry none.
   */
  ariaCurrent: "page" | "true" | undefined;
}

/** Fixed order, and the word each tab carries (the same words as the stage path). */
const TABS: readonly { key: CaseTabKey; label: string }[] = [
  { key: "akte", label: "Akte" },
  { key: "auslese", label: "Auslese" },
  { key: "umriss", label: "Umriss" },
  { key: "planung", label: "Planung" },
  { key: "kalkulation", label: "Kalkulation" },
];

/** `/vorgang/:id` and its one level below — the id is not needed here, only the leaf. */
const CASE_PAGE = /^\/vorgang\/[^/]+(?:\/([^/]+))?\/?$/;

/** The tab a leaf under `/vorgang/:id/` lights. `unterlagen` and the rest light none. */
const LEAF_TAB: Readonly<Partial<Record<string, CaseTabKey>>> = {
  eingang: "akte",
  einlesen: "auslese",
  // The reading's prüfansicht is part of the Auslese; „Schema" is no longer a word of the navigation.
  schema: "auslese",
  planung: "planung",
  kalkulation: "kalkulation",
};

/**
 * Which tab the page at `pathname` + `search` belongs to; `null` off a
 * Vorgang (and on the Vorgang's pages that no tab leads to).
 *
 * `/umriss` is two pages in one route: with `aus=lesung` it is the reading's
 * outline put right — a part of the Auslese —, otherwise it is the way to
 * Stand 1 by hand, or „Form ändern …" on a Vorgang that has one (`aus=stand`).
 * Without `case` it is a *new* Vorgang's drawing and belongs to none.
 */
export function tabOfPage(pathname: string, search: string): CaseTabKey | null {
  if (pathname === "/umriss" || pathname === "/umriss/") {
    const params = new URLSearchParams(search);
    if (!params.get("case")) return null;
    return params.get("aus") === "lesung" ? "auslese" : "umriss";
  }
  const match = CASE_PAGE.exec(pathname);
  if (match === null) return null;
  const leaf = match[1];
  if (leaf === undefined) return "akte";
  return (Object.hasOwn(LEAF_TAB, leaf) ? LEAF_TAB[leaf] : undefined) ?? null;
}

/** The page a tab leads to — the lit one too, except the lit „Umriss" (`caseTabs`). */
function entry(key: CaseTabKey, caseId: string): string {
  const id = encodeURIComponent(caseId);
  switch (key) {
    case "akte":
      return `/vorgang/${id}`;
    case "auslese":
      return `/vorgang/${id}/einlesen`;
    case "umriss":
      return `/umriss?case=${id}`;
    case "planung":
      return `/vorgang/${id}/planung`;
    case "kalkulation":
      return `/vorgang/${id}/kalkulation`;
  }
}

/**
 * Whether the page of `key` has something to show for these facts.
 *
 * „Das Stadium lässt Stand 1 noch zu" is the rule the Akte's head already
 * follows (`RecordHead` `over`): not `sent`, not finished. Past it the server
 * would refuse the first Stand with 409, so the two ways to it are not shown.
 */
function hasSomethingToShow(key: CaseTabKey, facts: CaseFacts): boolean {
  const open = facts.status !== "sent" && !HANDOVER_CLOSED.has(facts.status);
  switch (key) {
    case "akte":
      return true;
    case "auslese":
      return facts.drawings > 0 && facts.stand === 0 && open;
    case "umriss":
      return facts.drawings === 0 && facts.stand === 0 && open;
    case "planung":
    case "kalkulation":
      return facts.stand >= 1;
  }
}

/**
 * The tabs of one Vorgang, in order. `facts` is `null` while the answer is
 * out: then only the Akte and the page's own tab stand, so nothing shows up
 * for a moment and goes again. `here` is the page's tab (`tabOfPage`),
 * `currentUrl` its `pathname + search`.
 *
 * Every tab leads to its own page, the lit one too (ruling R-5): the Eingang
 * lights „Akte", and „Akte" is its way to the record; the reading's pages
 * light „Auslese", and it leads to /einlesen. Only the lit „Umriss" stays on
 * the page as it stands — on `aus=stand` its entry would open the drawing of
 * Stand 1 in place of „Form ändern …".
 */
export function caseTabs(caseId: string, facts: CaseFacts | null, here: CaseTabKey | null, currentUrl: string): CaseTab[] {
  const shown = (key: CaseTabKey) => key === here || key === "akte" || (facts !== null && hasSomethingToShow(key, facts));
  return TABS.filter(({ key }) => shown(key)).map(({ key, label }) => {
    const current = key === here;
    const to = current && key === "umriss" ? currentUrl : entry(key, caseId);
    const ariaCurrent = current ? (to === currentUrl ? "page" : "true") : undefined;
    return { key, label, to, current, ariaCurrent };
  });
}
